import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '~/components/Modal/Modal';

interface DirtyFormContextProps {
  registerDirty: (key: string) => void;
  clearDirty: (key: string) => void;
  hasDirty: () => boolean;
  confirmIfDirty: (onConfirm: (wasDirty: boolean) => void) => void;
}

const defaultValues: DirtyFormContextProps = {
  registerDirty: null,
  clearDirty: null,
  hasDirty: null,
  confirmIfDirty: null,
};

const DirtyFormContext = createContext<DirtyFormContextProps>(defaultValues);

interface DirtyFormProviderProps {
  children: ReactNode;
}

export const DirtyFormProvider: FC<DirtyFormProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const dirtyKeysRef = useRef<Set<string>>(new Set());
  const pendingActionRef = useRef<(() => void) | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const registerDirty = useCallback((key: string) => {
    dirtyKeysRef.current.add(key);
  }, []);

  const clearDirty = useCallback((key: string) => {
    dirtyKeysRef.current.delete(key);
  }, []);

  const hasDirty = useCallback(() => dirtyKeysRef.current.size > 0, []);

  const confirmIfDirty = useCallback(
    (onConfirm: (wasDirty: boolean) => void) => {
      if (hasDirty()) {
        pendingActionRef.current = () => onConfirm(true);
        setIsConfirmOpen(true);
        return;
      }
      onConfirm(false);
    },
    [hasDirty],
  );

  const handleLeave = () => {
    setIsConfirmOpen(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  };

  const handleStay = () => {
    setIsConfirmOpen(false);
    pendingActionRef.current = null;
  };

  return (
    <DirtyFormContext.Provider value={{ registerDirty, clearDirty, hasDirty, confirmIfDirty }}>
      {children}
      <Modal
        open={isConfirmOpen}
        title={t('common.unsavedChangesTitle')}
        submitButtonTitle={t('common.leavePage')}
        cancelButtonTitle={t('common.stayOnPage')}
        reverseActionsOrder
        onClose={handleStay}
        onCancel={handleStay}
        onSubmit={handleLeave}
      >
        {t('common.unsavedChangesDescription')}
      </Modal>
    </DirtyFormContext.Provider>
  );
};

export const useDirtyFormContext: () => DirtyFormContextProps = () => {
  return useContext(DirtyFormContext);
};

// Lets a create/edit form register its dirty state so a global org switch (or any other
// navigation-guarding action) can prompt an "unsaved changes" confirmation first.
export const useDirtyForm = (key: string, isDirty: boolean) => {
  const { registerDirty, clearDirty } = useDirtyFormContext();

  useEffect(() => {
    if (isDirty) {
      registerDirty(key);
    } else {
      clearDirty(key);
    }
  }, [isDirty, key, registerDirty, clearDirty]);

  useEffect(() => {
    return () => clearDirty(key);
  }, [key, clearDirty]);
};
